import React, { useState } from 'react';
import { X, Plus, Minus, AlertCircle, CheckCircle } from 'lucide-react';
import { BASE_URL } from '../../constant/constant';

const TokenManagementModal = ({
  isOpen,
  onClose,
  user,
  onSuccess,
  type = 'grant' // 'grant' or 'revoke'
}) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('7_days');
  const [customDate, setCustomDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const calculateExpiresAt = () => {
        if (duration === 'custom') return customDate;

        const now = new Date();
        switch (duration) {
          case '7_days': return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
          case '14_days': return new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString();
          case '1_month': return new Date(now.setMonth(now.getMonth() + 1)).toISOString();
          case '3_months': return new Date(now.setMonth(now.getMonth() + 3)).toISOString();
          case '6_months': return new Date(now.setMonth(now.getMonth() + 6)).toISOString();
          case '12_months': return new Date(now.setFullYear(now.getFullYear() + 1)).toISOString();
          default: return null;
        }
      };

      const tokenData = {
        amount: parseInt(amount),
        reason: reason.trim(),
        tokenExpiration: calculateExpiresAt()
      };

      // Import the service function dynamically to avoid circular imports
      const { grantTokens, revokeTokens } = await import('../../services/adminService');

      const response = type === 'grant'
        ? await grantTokens(user._id, tokenData)
        : await revokeTokens(user._id, tokenData);

      if (response) {
        onSuccess(response);
        handleClose();
      }
    } catch (err) {
      setError(err.message || `Failed to ${type} tokens`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setReason('');
    setError('');
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${type === 'grant' ? 'bg-green-100' : 'bg-red-100'
              }`}>
              {type === 'grant' ? (
                <Plus className="w-5 h-5 text-green-600" />
              ) : (
                <Minus className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {type === 'grant' ? 'Grant Tokens' : 'Revoke Tokens'}
              </h3>
              <p className="text-sm text-gray-500">
                {type === 'grant' ? 'Add tokens to user account' : 'Remove tokens from user account'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            {user.profilePicture?.url ? (
              <img
                src={BASE_URL + user.profilePicture.url}
                alt={user.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                </span>
              </div>
            )}
            <div>
              <p className="font-medium text-gray-900">{user.name}</p>
              <p className="text-sm text-gray-500">{user.email}</p>
              <p className="text-sm text-gray-600">
                Current tokens: <span className="font-semibold">{user.tokens?.toLocaleString() || 0}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {type === 'grant' ? 'Amount to Grant' : 'Amount to Revoke'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <span className="text-gray-500 text-sm">tokens</span>
                </div>
              </div>
            </div>

            {/* Duration */}
            {type === 'grant' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration
                  </label>
                  <select
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent"
                  >
                    <option value="7_days">7 Days</option>
                    <option value="14_days">14 Days</option>
                    <option value="1_month">1 Month</option>
                    <option value="3_months">3 Months</option>
                    <option value="6_months">6 Months</option>
                    <option value="12_months">12 Months</option>
                    <option value="custom">Custom Date</option>
                  </select>
                </div>

                {duration === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Custom Date
                    </label>
                    <input
                      type="date"
                      value={customDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setCustomDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent"
                      required={duration === 'custom'}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={`Enter reason for ${type === 'grant' ? 'granting' : 'revoking'} tokens...`}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#075038] focus:border-transparent resize-none"
                required
              />
            </div>

            {/* Preview */}
            {amount && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Preview</span>
                </div>
                <div className="text-sm text-blue-800">
                  <p>Current tokens: {user.tokens?.toLocaleString() || 0}</p>
                  <p>
                    {type === 'grant' ? 'After granting' : 'After revoking'}: {' '}
                    <span className="font-semibold">
                      {type === 'grant'
                        ? ((user.tokens || 0) + parseInt(amount || 0)).toLocaleString()
                        : Math.max(0, (user.tokens || 0) - parseInt(amount || 0)).toLocaleString()
                      }
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !amount || !reason.trim()}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${type === 'grant'
                ? 'bg-[#075038] hover:bg-[#075038]/90 disabled:bg-[#075038]/50'
                : 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                } disabled:cursor-not-allowed`}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{type === 'grant' ? 'Granting...' : 'Revoking...'}</span>
                </div>
              ) : (
                `${type === 'grant' ? 'Grant' : 'Revoke'} Tokens`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TokenManagementModal;
